// Pure schema logic for the Attendance Allowance question set: flattening,
// section ordering and labels. No Vite/browser APIs here so the same code can
// run in the browser (via lib/aaSchema.ts + import.meta.glob) AND inside the
// Vite dev-server process (server/extract.ts loads the JSONs with node:fs).

export type FieldKind =
  | 'text'
  | 'textarea'
  | 'date'
  | 'select'
  | 'boolean'
  | 'multiselect'
  | 'address'
  | 'table'
  | 'number';

export interface SchemaOption {
  id: string;
  label: string;
}

export interface FieldCondition {
  operator?: string; // 'equal' | 'not-equal'
  value?: unknown;
}

export interface FlatField {
  id: string;
  label: string;
  kind: FieldKind;
  options?: SchemaOption[];
  tooltip?: string;
  columns?: FlatField[]; // for kind === 'table'
  depth: number; // nesting depth for indent
  conditional: boolean; // shown only if a parent answer triggers it
  required: boolean;
  parentId?: string; // the question whose answer triggers this sub-question
  condition?: FieldCondition; // trigger condition against the parent's answer
}

export interface FormSection {
  key: string;
  label: string;
  fields: FlatField[];
}

export interface RawField {
  type?: string;
  options?: SchemaOption[];
}
export interface RawQuestion {
  id?: string;
  label?: string;
  tooltip?: string | null;
  isRequired?: boolean;
  field?: RawField;
  subQuestions?: { condition?: unknown; question: RawQuestion }[];
}

const KIND: Record<string, FieldKind> = {
  text: 'text',
  date: 'date',
  boolean: 'boolean',
  select: 'select',
  address: 'address',
  number: 'number',
  'question-table': 'table',
  'multi-select-box': 'multiselect',
  'multi-select': 'multiselect',
  'slider-select': 'select',
};

function kindOf(type?: string): FieldKind {
  return (type && KIND[type]) || 'text';
}

function flatten(
  q: RawQuestion | undefined,
  depth: number,
  conditional: boolean,
  out: FlatField[],
  parentId?: string,
  condition?: FieldCondition,
): void {
  if (!q || !q.id) return;
  const kind = kindOf(q.field?.type);
  const required = q.isRequired === true;
  if (kind === 'table') {
    const columns: FlatField[] = [];
    for (const sq of q.subQuestions ?? [])
      flatten(sq.question, 0, false, columns);
    out.push({
      id: q.id,
      label: q.label ?? q.id,
      kind,
      columns,
      tooltip: q.tooltip ?? undefined,
      depth,
      conditional,
      required,
      parentId,
      condition,
    });
    return; // a table owns its columns; don't emit them as standalone fields
  }
  out.push({
    id: q.id,
    label: q.label ?? q.id,
    kind,
    options: q.field?.options,
    tooltip: q.tooltip ?? undefined,
    depth,
    conditional,
    required,
    parentId,
    condition,
  });
  for (const sq of q.subQuestions ?? []) {
    flatten(
      sq.question,
      depth + 1,
      true,
      out,
      q.id,
      sq.condition as FieldCondition | undefined,
    );
  }
}

function conditionMet(
  cond: FieldCondition | undefined,
  parentValue: unknown,
): boolean {
  if (!cond || cond.operator === undefined)
    return parentValue != null && parentValue !== '';
  const cv = cond.value;
  // multi-select parents: equal means "includes the option", not-equal means
  // "answered and doesn't include it" (e.g. falls: anything except "No").
  if (Array.isArray(parentValue)) {
    const has = typeof cv === 'string' && parentValue.includes(cv);
    return cond.operator === 'not-equal' ? parentValue.length > 0 && !has : has;
  }
  if (cond.operator === 'not-equal')
    return parentValue != null && parentValue !== '' && parentValue !== cv;
  return parentValue === cv;
}

/**
 * A field is active (applicable on the form) when every ancestor's trigger
 * condition is satisfied by the current answers. Depth-0 fields are always
 * active. Inactive fields are hidden from review AND excluded from the PDF,
 * so flipping a parent answer can't leave contradictory sub-answers behind.
 */
export function isFieldActive(
  field: FlatField,
  fieldsById: Record<string, FlatField>,
  getValue: (questionId: string) => unknown,
): boolean {
  let cur: FlatField | undefined = field;
  while (cur?.parentId) {
    const parent: FlatField | undefined = fieldsById[cur.parentId];
    if (!parent) return true;
    if (!conditionMet(cur.condition, getValue(parent.id))) return false;
    cur = parent;
  }
  return true;
}

// Ordered section labels keyed by schema file basename (without extension).
export const SECTION_LABELS: Record<string, string> = {
  '1-about-you': 'About the person',
  '2-signing-form-for-someone-else': 'Signing for someone else',
  '3-1-about-illnesses': 'Illnesses and disabilities',
  '3-2-about-gp': 'GP and other professionals',
  '3-3-consent': 'Consent',
  '3-4-more-illness-info': 'Tests, surgery and aids',
  '4-care-needs': 'When care needs started',
  '5-help-with-care-needs-during-the-day': 'Help needed during the day',
  '6-help-with-care-needs-during-the-night': 'Help needed during the night',
  '7-help-with-care-needs': 'Anything else about care needs',
  '8-about-time-spent-in-hospital': 'Hospital or care home',
  '9-constant-attendance-allowance': 'Constant Attendance Allowance',
  '10-how-we-pay-you': 'How we pay you',
  '11-statement-from-someone-who-knows-you':
    'Statement from someone who knows them',
  '12-extra-information': 'Extra information',
  '13-declaration': 'Declaration',
  '14-what-to-do-now': 'What to do now',
};

// numeric-prefix sort: "3-1" < "3-2" < "10"
export function orderKey(base: string): number {
  const m = base.match(/^(\d+)(?:-(\d+))?/);
  if (!m) return 9999;
  return Number(m[1]) * 100 + (m[2] ? Number(m[2]) : 0);
}

/** Build ordered, labelled sections from {fileBasename -> questions}. */
export function buildSections(
  files: Record<string, RawQuestion[]>,
): FormSection[] {
  return Object.entries(files)
    .sort(([a], [b]) => orderKey(a) - orderKey(b))
    .map(([base, questions]) => {
      const fields: FlatField[] = [];
      for (const q of questions ?? []) flatten(q, 0, false, fields);
      return { key: base, label: SECTION_LABELS[base] ?? base, fields };
    })
    .filter((s) => s.fields.length > 0);
}

/** Flat index of every field by id (includes table columns). */
export function buildFieldIndex(
  sections: FormSection[],
): Record<string, FlatField> {
  const idx: Record<string, FlatField> = {};
  const add = (f: FlatField) => {
    idx[f.id] = f;
    f.columns?.forEach(add);
  };
  sections.forEach((s) => s.fields.forEach(add));
  return idx;
}

/** Replace {careRecipient}-style placeholders with the client's name. */
export function interpolate(label: string, name: string): string {
  return label.replace(/\{\s*care[rR]ecipient\s*\}/g, name);
}
